import { useTranslation } from "react-i18next";
import { Card } from "reactstrap";
import Drawer from "../../../../components/Drawer";
import NTable from "../../../../components/NTable";
import Actions from "../../../../components/NTable/Actions";
import { useNewTable } from "../../../../components/NTable/hook";
import { useAccess, useActionWithAccess } from "../../../../utils/hooks/access";
import BulkActions from "../../components/BulkActions";
import Topbar from "../../components/Topbar";
import { useDeleteModal } from "../../helpers/useDeleteModal";
import { useDrawerProps } from "../../helpers/useDrawerProps";
import { useFetchResource } from "../../helpers/useFetchResource";
import { useManageForm } from "../../helpers/useManageForm";
import useSettingsCRUDHook from "../../hook";
import { useColumns } from "./columns";
import Form from "./Form";

function LocationRule() {
    const { t } = useTranslation();

    const {
        state: { mode, data, totalItems, loading, submitting, error },
        selected,
        fetch,
        createSubmit,
        updateSubmit,
        goToHelp,
        goToCreate,
        goToEdit,
        goToClone,
        goToView,
        remove,
        batchRemove,
        loadingExport,
    } = useSettingsCRUDHook("/allocation-rules");

    const { hasAccess: canAdd } = useAccess("settings.rules.allocationRules.canCreate");

    const { modal: deleteModal, open: openDeleteModal } = useDeleteModal({
        message: (data) =>
            Array.isArray(data)
                ? t("do-you-want-to-delete-location-rules", {
                    count: data?.length,
                })
                : t("do-you-want-to-delete-location-rule"),
        onConfirm: (data) => (Array.isArray(data) ? batchRemove(data) : remove(data)),
        permission: "settings.rules.allocationRules.canDelete",
    });

    const { unarchiveModal, isFormOpen, formProps } = useManageForm({
        mode,
        selected,
        createSubmit,
        updateSubmit,
    });

    const columns = useColumns({
        edit: useActionWithAccess(goToEdit, "settings.rules.allocationRules.canEdit"),
        remove: openDeleteModal,
        clone: useActionWithAccess(goToClone, "settings.rules.allocationRules.canCreate"),
    });

    const table = useNewTable({
        data,
        columns,
        loading,
        totalItems,
        sessionFiltersKey: "allocationRulesLocation",
    });

    const { open: drawerOpen, title: drawerTitle } = useDrawerProps({
        mode,
        help: t("help"),
        view: t("view-location-rule"),
        create: t("add-location-rule"),
        edit: t("edit-location-rule"),
        clone: t("clone-location-rule"),
    });

    useFetchResource({ fetch, state: table.getState(), type: "location" });

    return (
        <div className="d-flex h-100">
            <Card className="flex-fill mb-0">
                <Topbar
                    title={t("location-rules")}
                    add={canAdd && goToCreate}
                    help={goToHelp}
                    loading={loading || loadingExport}
                />

                <Card className="mb-auto" style={{ maxHeight: "calc(100% - 156px)" }}>
                    <Actions
                        filters={table.getState().columnFilters}
                        setFilters={table.setColumnFilters}
                        columns={table.getAllColumns()}
                    >
                        <BulkActions rows={table.getState().rowSelection} batchRemove={openDeleteModal} />
                    </Actions>
                    <NTable table={table} />
                </Card>
            </Card>

            <Drawer open={drawerOpen} close={goToView} title={drawerTitle}>
                {isFormOpen ? (
                    <Form {...formProps} error={error} loading={submitting} close={goToView} />
                ) : (
                    <>{t("help")}</>
                )}
            </Drawer>

            {deleteModal}

            {unarchiveModal}
        </div>
    );
}

export default LocationRule;
